import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { SERVICES_DATA, COMPANY_INFO, SEO_CONFIG } from '../data/companyData';
import { getOptimizedImageUrl } from '../lib/imageOptimizer';

export default function ServicesPage() {
  return (
    <>
      <SEOHead
        title={SEO_CONFIG.services.title}
        description={SEO_CONFIG.services.description}
        image="https://assets.auxodev.dk/assets/9badcd2d-12ea-450b-8280-42a177052604/hero-climate-refrigeration.jpg"
      />

      {/* Hero Header */}
      <section className="bg-neutral-950 text-white py-16 sm:py-20 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-neutral-900 border border-neutral-800 text-[#DFBF77] text-xs font-semibold uppercase tracking-wider shadow-xs">
                <i className="fa-solid fa-layer-group" aria-hidden="true" />
                Ydelsesoversigt
              </div>
              <h1 className="mt-4 pt-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
                Vores specialiserede <span className="text-[#DFBF77]">ydelser & klimaløsninger</span>
              </h1>
            </div>
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
              Morine Køl & Klimateknik tilbyder rådgivning, installation og vedligeholdelse af professionelle køle-, frost- og klimaanlæg. Vi tilpasser hver enkelt løsning til din branches specifikke krav til driftssikkerhed og energieffektivitet.
            </p>
          </div>
        </div>
      </section>

      {/* All Services Detailed List */}
      <section className="py-16 sm:py-20 bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {SERVICES_DATA.map((service, idx) => {
            const isReversed = idx % 2 !== 0;

            return (
              <div
                key={service.slug}
                className="bg-white rounded-2xl border border-neutral-200/80 shadow-xs overflow-hidden"
              >
                <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center p-6 sm:p-8 lg:p-10 ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Service Image */}
                  <div className={`lg:col-span-5 ${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-neutral-900 border border-neutral-200">
                      <img
                        src={getOptimizedImageUrl(service.heroImage, { width: 800, height: 600 })}
                        alt={service.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Service Text */}
                  <div className={`lg:col-span-7 space-y-5 ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#FAF6ED] border border-[#EAD5A5] text-[#8D6D2F] flex items-center justify-center text-base">
                        <i className={service.icon} aria-hidden="true" />
                      </div>
                      <div>
                        <span className="inline-block text-xs font-bold text-[#8D6D2F] uppercase tracking-wider">
                          Specialområde #{idx + 1}
                        </span>
                        <h2 className="mt-1 pt-0.5 text-2xl sm:text-3xl font-bold tracking-tight text-neutral-950">
                          {service.title}
                        </h2>
                      </div>
                    </div>

                    <p className="text-neutral-700 text-sm sm:text-base leading-relaxed">
                      {service.description}
                    </p>

                    {/* Environmental / Key Points with green checkmarks */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                      {service.environmentalPoints.map((pt, pIdx) => (
                        <div
                          key={pIdx}
                          className="flex items-center gap-2.5 p-2.5 rounded-xl bg-neutral-50/80 border border-neutral-200/60 text-xs sm:text-sm font-medium text-neutral-800"
                        >
                          <div className="w-5 h-5 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center flex-shrink-0 text-emerald-700 text-[10px]">
                            <i className="fa-solid fa-check" aria-hidden="true" />
                          </div>
                          <span>{pt}</span>
                        </div>
                      ))}
                    </div>

                    {/* Link to Dedicated Service Page */}
                    <div className="pt-3 flex flex-wrap items-center gap-4">
                      <Link
                        to={`/ydelser/${service.slug}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-neutral-950 hover:bg-[#20252D] text-white text-xs sm:text-sm font-semibold transition-all shadow-xs group border border-neutral-800"
                      >
                        <span>Læs mere om {service.shortTitle}</span>
                        <i className="fa-solid fa-arrow-right text-[11px] text-[#DFBF77] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                      </Link>

                      <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-[#8D6D2F] hover:text-[#B08B3E] transition-colors"
                      >
                        <span>Indhent tilbud</span>
                        <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
